import React, { useState, useEffect, useRef } from "react";
import heroImg1 from "../../assets/images/hero-placeholder/1.jpg";
import heroImg2 from "../../assets/images/hero-placeholder/2.jpg";
import heroImg3 from "../../assets/images/hero-placeholder/3.jpg";
import useNewReleases from "../../hooks/useNewReleases";

const placeholderSlides = [
  { image: heroImg1, caption: "Ninja High School Issue 202!", linkTo: null },
  { image: heroImg2, caption: "Cryptid Corps - 1 of 6!", linkTo: null },
  { image: heroImg3, caption: "Pounds of Power - 2 of 4!", linkTo: null },
];

export default function HeroCarousel() {
  const { slides: liveSlides, loading } = useNewReleases();
  const slides =
    !loading && Array.isArray(liveSlides) && liveSlides.length > 0
      ? liveSlides
      : placeholderSlides;

  const [current, setCurrent] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const intervalRef = useRef(null);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const startAuto = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 3000);
  };

  const stopAuto = () => clearInterval(intervalRef.current);

  useEffect(() => {
    setCurrent(0);
    startAuto();
    return () => stopAuto();
  }, [slides.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const goPrev = () => setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
  const goNext = () => setCurrent((prev) => (prev + 1) % slides.length);
  const goTo = (idx) => setCurrent(idx);

  const slideHeight = isMobile ? "240px" : "420px";

  const outerStyle = {
    position: "relative",
    width: "100%",
    overflow: "hidden",
    borderRadius: "12px",
    marginBottom: "16px",
  };

  const trackStyle = {
    position: "relative",
    height: slideHeight,
  };

  const getSlideStyle = (isActive) => ({
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: isActive ? 1 : 0,
    transition: "opacity 400ms ease",
    pointerEvents: isActive ? "auto" : "none",
  });

  const arrowBtnBase = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 3,
    background: "rgba(0,0,0,0.45)",
    border: "none",
    borderRadius: "50%",
    width: "48px",
    height: "48px",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontSize: "2rem",
    lineHeight: 1,
    cursor: "pointer",
  };

  const dotsWrapStyle = {
    display: "flex",
    justifyContent: "center",
    gap: "8px",
    padding: "10px 0 4px",
  };

  const getDotStyle = (isActive) => ({
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    backgroundColor: isActive ? "#ffffff" : "transparent",
    border: "2px solid rgba(255,255,255,0.7)",
    cursor: "pointer",
    transition: "background-color 200ms ease",
    padding: 0,
  });

  return (
    <div style={outerStyle} onMouseEnter={stopAuto} onMouseLeave={startAuto}>
      <div style={trackStyle}>
        {slides.map((slide, idx) => {
          const isActive = idx === current;
          const SlideTag = slide.linkTo ? "a" : "div";
          const slideTagProps = slide.linkTo
            ? { href: slide.linkTo, style: { ...getSlideStyle(isActive), textDecoration: "none" } }
            : { style: getSlideStyle(isActive) };

          return (
            <SlideTag key={idx} {...slideTagProps}>
              {/* Slide background */}
              <div style={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${slide.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center center",
                backgroundRepeat: "no-repeat",
              }} />

              {/* Per-slide caption overlay */}
              {slide.caption && (
                <div style={{
                  position: "absolute",
                  bottom: isMobile ? "44px" : "60px",
                  left: 0,
                  right: 0,
                  padding: "12px 20px",
                  backgroundColor: "rgba(0, 0, 0, 0.55)",
                  backdropFilter: "blur(2px)",
                  textAlign: "center",
                  zIndex: 3,
                }}>
                  <p style={{
                    color: "#ffffff",
                    fontSize: "1rem",
                    fontStyle: "italic",
                    letterSpacing: "0.03em",
                    margin: 0,
                  }}>
                    {slide.caption}
                  </p>
                </div>
              )}

              {/* Caption bar — lower third */}
              <div style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: "black",
                padding: isMobile ? "10px 16px" : "14px 24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2,
              }}>
                <p style={{
                  margin: 0,
                  color: "#ffffff",
                  fontWeight: 700,
                  fontSize: isMobile ? "1.2rem" : "1.8rem",
                  textShadow: "0 2px 6px rgba(0,0,0,0.6)",
                }}>
                  Hot off the Coolest Presses!
                </p>
              </div>
            </SlideTag>
          );
        })}

        {/* Prev arrow */}
        <button
          style={{ ...arrowBtnBase, left: "12px" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.72)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.45)"; }}
          onClick={goPrev}
          aria-label="Previous slide"
        >
          &#8249;
        </button>

        {/* Next arrow */}
        <button
          style={{ ...arrowBtnBase, right: "12px" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.72)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.45)"; }}
          onClick={goNext}
          aria-label="Next slide"
        >
          &#8250;
        </button>
      </div>

      {/* Dot indicators */}
      <div style={dotsWrapStyle}>
        {slides.map((_, idx) => (
          <button
            key={idx}
            style={getDotStyle(idx === current)}
            onClick={() => goTo(idx)}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
